import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useState, useEffect } from 'react';
import { useClient } from '../ClientContext';

const styles = {
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    mb: '32px',
    p: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  subtitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  title: {
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '24px',
    mb: '8px',
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#344054',
  },
  lastEdited: {
    fontSize: '12px',
    fontStyle: 'italic',
    lineHeight: '20px',
    color: '#98A2B3',
    textAlign: 'right',
  },
  buttonOn: {
    backgroundColor: '#772EDD',
    color: '#FFFFFF',
    textTransform: 'none',
    fontWeight: 'bold',
    boxShadow: 'none',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#5C21BF',
    },
  },
  buttonOff: {
    color: '#475467',
    backgroundColor: 'transparent',
    border: '1px solid #475467',
    textTransform: 'none',
    fontWeight: 'bold',
    boxShadow: 'none',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#F3EAFF',
    },
  },
};

const MaintenanceModeCard = ({ accessToken }) => {
  const [maintenanceMode, setMaintenanceMode] = useState(null);
  const [lastChanged, setLastChanged] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { selectedClient } = useClient();

  useEffect(() => {
    const fetchMaintenanceStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/admin/maintenance-mode?client=${encodeURIComponent(selectedClient)}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        setMaintenanceMode(data?.data?.isEnabled ?? null);
        setLastChanged(data?.data?.updatedAt ? new Date(data?.data?.updatedAt) : null);
        setError(null);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error:', error);
        setError(error.message);
      }
    };

    if (
      selectedClient === 'Tidal' ||
      selectedClient === 'SoundExchange' ||
      selectedClient === 'MLC' ||
      selectedClient === 'MLC Claims' ||
      selectedClient === 'iHeart' ||
      selectedClient === 'Beatport' ||
      selectedClient === 'Audiomack' ||
      selectedClient === 'RocNation'
    ) {
      fetchMaintenanceStatus();
    }
  }, [selectedClient, accessToken]);

  const handleButtonClick = () => {
    const newMode = !maintenanceMode;
    setLoading(true);
    setError(null);

    fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/maintenance-mode`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        isEnabled: newMode,
        client: selectedClient,
      }),
    })
      .then((response) => {
        setLoading(false);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(() => {
        setMaintenanceMode(newMode);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error:', error);
        setError(error.message);
      });
  };

  const formattedLastChanged = lastChanged ? lastChanged.toLocaleString() : 'N/A';

  return (
    <>
      <Box sx={styles.container}>
        <Box sx={styles.leftContainer}>
          <Typography sx={styles.title}>Maintenance</Typography>
          <Box sx={styles.subtitleContainer}>
            <Typography sx={styles.subtitle}>
              Maintenance mode is currently {maintenanceMode === null ? 'loading...' : maintenanceMode ? 'ON' : 'OFF'}
            </Typography>
            {selectedClient && <Typography sx={styles.subtitle}>Client: {selectedClient}</Typography>}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {maintenanceMode !== null && (
            <Typography sx={styles.lastEdited}>Last changed at {formattedLastChanged}</Typography>
          )}
          {error && (
            <Typography color="error" variant="body2">
              Error: {error}
            </Typography>
          )}
          <Button
            variant={maintenanceMode ? 'outlined' : 'contained'}
            sx={maintenanceMode ? styles.buttonOff : styles.buttonOn}
            onClick={handleButtonClick}
            disabled={loading || maintenanceMode === null || selectedClient === 'All Dashboards'}
          >
            {loading ? 'Processing...' : maintenanceMode ? 'Turn OFF Maintenance Mode' : 'Turn ON Maintenance Mode'}
          </Button>
        </Box>
      </Box>
      {selectedClient !== 'SoundExchange' &&
        selectedClient !== 'MLC' &&
        selectedClient !== 'MLC Claims' &&
        selectedClient !== 'RocNation' && <GlobalMaintenanceModeCard accessToken={accessToken} />}
    </>
  );
};

const GlobalMaintenanceModeCard = ({ accessToken }) => {
  const [maintenanceMode, setMaintenanceMode] = useState(null);
  const [lastChanged, setLastChanged] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clients = ['Tidal', 'iHeart', 'Beatport', 'Audiomack'];
  const [clientStatuses, setClientStatuses] = useState({});

  useEffect(() => {
    const fetchAllMaintenanceStatuses = async () => {
      try {
        const statuses = {};
        let lastUpdate = null;

        for (const client of clients) {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/admin/maintenance-mode?client=${encodeURIComponent(client)}`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            },
          );

          if (!response.ok) {
            throw new Error(`Network response was not ok for ${client}`);
          }

          const data = await response.json();
          statuses[client] = data?.data?.isEnabled ?? null;

          if (data?.data?.updatedAt) {
            const updateTime = new Date(data?.data?.updatedAt);
            if (!lastUpdate || updateTime > lastUpdate) {
              lastUpdate = updateTime;
            }
          }
        }

        const allEnabled = clients.every((client) => statuses[client] === true);
        setMaintenanceMode(allEnabled);
        setClientStatuses(statuses);
        setLastChanged(lastUpdate);
        setError(null);
      } catch (error) {
        console.error('Error:', error);
        setError(error.message);
      }
    };

    fetchAllMaintenanceStatuses();
  }, [accessToken]);

  const handleGlobalButtonClick = () => {
    const newMode = !maintenanceMode;
    setLoading(true);
    setError(null);

    const updatePromises = clients.map((client) =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/maintenance-mode`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isEnabled: newMode,
          client: client,
        }),
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`Network response was not ok for ${client}`);
        }
        return response.json();
      }),
    );

    Promise.all(updatePromises)
      .then(() => {
        const newStatuses = {};
        clients.forEach((client) => {
          newStatuses[client] = newMode;
        });
        setClientStatuses(newStatuses);
        setMaintenanceMode(newMode);
        setLastChanged(new Date());
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error:', error);
        setError(error.message);
        setLoading(false);
      });
  };

  const formattedLastChanged = lastChanged ? lastChanged.toLocaleString() : 'N/A';
  const clientStatusText = Object.entries(clientStatuses)
    .map(([client, status]) => `${client}: ${status ? 'ON' : 'OFF'}`)
    .join(', ');

  return (
    <Box sx={styles.container}>
      <Box sx={styles.leftContainer}>
        <Typography sx={styles.title}>Global Maintenance</Typography>
        <Box sx={styles.subtitleContainer}>
          <Typography sx={styles.subtitle}>
            Global maintenance mode is currently{' '}
            {maintenanceMode === null ? 'loading...' : maintenanceMode ? 'ON' : 'OFF'}
          </Typography>
        </Box>
        {Object.keys(clientStatuses).length > 0 && (
          <Typography sx={{ ...styles.subtitle, mt: 1, fontSize: '12px' }}>
            Client statuses: {clientStatusText}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        {lastChanged && <Typography sx={styles.lastEdited}>Last changed at {formattedLastChanged}</Typography>}
        {error && (
          <Typography color="error" variant="body2">
            Error: {error}
          </Typography>
        )}
        <Button
          variant={maintenanceMode ? 'outlined' : 'contained'}
          sx={maintenanceMode ? styles.buttonOff : styles.buttonOn}
          onClick={handleGlobalButtonClick}
          disabled={loading || maintenanceMode === null}
        >
          {loading ? 'Processing...' : maintenanceMode ? 'Turn OFF All Maintenance' : 'Turn ON All Maintenance'}
        </Button>
      </Box>
    </Box>
  );
};

export default MaintenanceModeCard;
